import React, { FC, useState } from 'react';

import { Alert, Box, Button, Card, CardContent } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import TagIcon from '@mui/icons-material/Tag';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import IconCardHeader from '../../../sharedModules/layout/IconCardHeader';
import RequiredFieldRow from '../../components/RequiredFieldRow';

import { BackflowJobItem } from '../../model';
import axios from '../../../sharedModules/utils/axios.utils';

interface DeviceAccountNumberSectionProps {
  backflowJobItem: BackflowJobItem;
  fetchBackflowJobItem: () => void;
}

const DeviceAccountNumberSection: FC<DeviceAccountNumberSectionProps> = ({ backflowJobItem, fetchBackflowJobItem }) => {
  const [value, setValue] = useState<string>(backflowJobItem?.u_bfp_acct || '');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const hasChanges = value !== (backflowJobItem?.u_bfp_acct || '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await axios.put(`/backflows/backflow_job_items/${backflowJobItem.id}`, {
        backflow_job_item: { u_bfp_acct: value },
      });

      setSuccessMessage('Device account number saved');
      fetchBackflowJobItem();
    } catch {
      setErrorMessage('Something went wrong saving the device account number');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card elevation={3}>
      <CardContent>
        <div style={{ marginBottom: 20 }}>
          <IconCardHeader icon={TagIcon} title="Device Account Number" />
        </div>
        <ErrorMessage message={errorMessage} />
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <RequiredFieldRow
                label="Device Account Number"
                value={value}
                onChange={setValue}
                required={backflowJobItem?.requires_backflow_device_account_number}
                disabled={submitting}
                maxLength={30}
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={submitting || !hasChanges}
              sx={{ flexShrink: 0 }}
            >
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default DeviceAccountNumberSection;
